import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Empty,
  Upload,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PaperClipOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import RichTextEditor from '../../../components/richTextEditor/RichTextEditor';
import TaskDeleteModal from '../delete/TaskDeleteModal';
import { uploadFile } from '../../../utils/uploadFile';
import './TaskList.css';

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY h:mm A') : '—';
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY') : '—';
};

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i;
function isImageAttachment(att) {
  if (!att?.url) return false;
  if (att.url.startsWith('data:image/')) return true;
  return att.name && IMAGE_EXTENSIONS.test(att.name);
}

function openAttachmentUrl(url) {
  if (!url || typeof url !== 'string') return;
  if (url.startsWith('data:')) {
    try {
      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        })
        .catch(() => window.open(url, '_blank', 'noopener,noreferrer'));
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_SIZE_MB = 2;

const REWARD_POINT_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'done', label: 'Done' },
];

const TASK_TYPE_OPTIONS = [
  { value: 'one-time', label: 'One Time' },
  { value: 'recurring', label: 'Recurring' },
];

function isHtml(str) {
  if (typeof str !== 'string') return false;
  return /<[a-z][\s\S]*>/i.test(str);
}

function descriptionPreview(description, maxLen = 60) {
  if (!description || typeof description !== 'string') return '—';
  const text = isHtml(description)
    ? description.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    : description.trim();
  if (!text) return '—';
  return text.length <= maxLen ? text : `${text.slice(0, maxLen)}…`;
}

export default function TaskList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [modalMode, setModalMode] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get('/users', { params: { limit: 500, assignableOnly: true } });
      const list = res.data?.data ?? [];
      setUserOptions(list.map((u) => ({ value: u._id, label: u.name || u.email || u._id })));
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to load users';
      dispatch(setMessage({ message: msg, type: 'error' }));
    }
  }, [dispatch]);

  const fetchTasks = useCallback(
    async (page, limit, search, status, assignedTo) => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (search && search.trim()) params.search = search.trim();
        if (status) params.status = status;
        if (assignedTo) params.assignedTo = assignedTo;
        const res = await axios.get('/tasks', { params });
        setTasks(res.data?.data ?? []);
        setPagination((prev) => ({
          ...prev,
          current: res.data?.page ?? page,
          pageSize: res.data?.limit ?? limit,
          total: res.data?.total ?? 0,
        }));
      } catch (err) {
        const msg = err.response?.data?.error || 'Failed to load tasks';
        dispatch(setMessage({ message: msg, type: 'error' }));
        setTasks([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const refetch = useCallback(() => {
    fetchTasks(
      pagination.current,
      pagination.pageSize,
      searchApplied,
      statusFilter,
      assignedFilter
    );
  }, [
    fetchTasks,
    pagination.current,
    pagination.pageSize,
    searchApplied,
    statusFilter,
    assignedFilter,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchTasks(
      pagination.current,
      pagination.pageSize,
      searchApplied,
      statusFilter,
      assignedFilter
    );
  }, [
    pagination.current,
    pagination.pageSize,
    searchApplied,
    statusFilter,
    assignedFilter,
    fetchTasks,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchApplied(searchInput.trim());
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (location.state?.openCreate) {
      navigate(location.pathname, { replace: true, state: {} });
      openCreateModal();
    }
  }, []);

  const onTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current ?? prev.current,
      pageSize: pag.pageSize ?? prev.pageSize,
    }));
  };

  const openCreateModal = () => {
    setSelectedTask(null);
    setModalMode('create');
    form.setFieldsValue({
      taskType: 'one-time',
      title: '',
      description: '',
      rewardPoint: 0,
      completedDate: null,
      assignedTo: undefined,
      attachments: [],
    });
  };

  const openEditModal = (task) => {
    setSelectedTask(task);
    setModalMode('edit');
    form.setFieldsValue({
      taskType: task.taskType ?? 'one-time',
      title: task.title ?? '',
      description: task.description ?? '',
      rewardPoint: task.rewardPoint ?? 0,
      completedDate: task.completedDate ? dayjs(task.completedDate) : null,
      assignedTo: task.assignedTo?._id ?? undefined,
      attachments: Array.isArray(task.attachments) ? [...task.attachments] : [],
    });
  };

  const openViewModal = (task) => {
    setSelectedTask(task);
    setModalMode('view');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedTask(null);
    form.resetFields();
  };

  const handleEditSubmit = async () => {
    if (modalMode === 'view') {
      closeModal();
      return;
    }
    try {
      const values = await form.validateFields();
      const attachmentsList = Array.isArray(values.attachments) ? values.attachments : [];
      const payload = {
        taskType: values.taskType ?? 'one-time',
        title: values.title?.trim() ?? '',
        description: typeof values.description === 'string' ? values.description.trim() : '',
        rewardPoint: values.rewardPoint ?? 0,
        completedDate: values.completedDate ? values.completedDate.toISOString() : null,
        assignedTo: values.assignedTo || null,
        attachments: attachmentsList,
      };
      setSubmitLoading(true);
      if (modalMode === 'create') {
        await axios.post('/tasks', payload);
        dispatch(setMessage({ message: 'Task created successfully', type: 'success' }));
        closeModal();
        refetch();
      } else if (selectedTask?._id) {
        await axios.put(`/tasks/${selectedTask._id}`, payload);
        dispatch(setMessage({ message: 'Task updated successfully', type: 'success' }));
        closeModal();
        refetch();
      }
    } catch (err) {
      const msg =
        err.response?.data?.error || (err.errorFields ? 'Please fix the form' : 'Request failed');
      if (err.response?.data?.error) dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    if (!taskId) return;
    setStatusUpdatingId(taskId);
    try {
      await axios.patch(`/tasks/${taskId}/status`, { status: newStatus });
      dispatch(setMessage({ message: 'Status updated', type: 'success' }));
      refetch();
    } catch (err) {
      const msg = err.response?.data?.error || 'Update failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/tasks/${taskToDelete._id}`);
      dispatch(setMessage({ message: 'Task deleted successfully', type: 'success' }));
      setTaskToDelete(null);
      refetch();
    } catch (err) {
      const msg = err.response?.data?.error || 'Delete failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      align: 'left',
      width: 300,
      render: (title) => <span className="task-list-cell-title">{title || '—'}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      align: 'left',
      ellipsis: true,
      width: 200,
      render: (desc) => (
        <span className="task-list-cell-description" title={descriptionPreview(desc, 200)}>
          {descriptionPreview(desc)}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'taskType',
      key: 'taskType',
      align: 'left',
      width: 100,
      render: (val) => TASK_TYPE_OPTIONS.find((o) => o.value === (val ?? 'one-time'))?.label ?? val ?? 'One Time',
    },
    {
      title: 'Reward',
      dataIndex: 'rewardPoint',
      key: 'rewardPoint',
      align: 'left',
      width: 90,
      render: (val) => <Tag color="gold">{val} pts</Tag>,
    },
    {
      title: 'Completed date',
      dataIndex: 'completedDate',
      key: 'completedDate',
      align: 'left',
      width: 150,
      render: (val) => formatDate(val),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      width: 140,
      render: (status, record) =>
        record.taskType === 'recurring' ? (
          '—'
        ) : (
          <Select
            value={status || 'open'}
            options={STATUS_OPTIONS}
            onChange={(value) => handleStatusChange(record._id, value)}
            loading={statusUpdatingId === record._id}
            size="small"
            style={{ minWidth: 110 }}
            dropdownMatchSelectWidth={false}
          />
        ),
    },
    {
      title: 'Assigned to',
      dataIndex: ['assignedTo', 'name'],
      key: 'assignedTo',
      align: 'left',
      width: 140,
      render: (_, record) =>
        record.assignedTo ? (
          <span>{record.assignedTo.name || record.assignedTo.email || '—'}</span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      align: 'left',
      width: 160,
      render: (val) => formatDateTime(val),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              className="app-btn-delete"
              onClick={() => handleDeleteClick(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const isModalOpen = modalMode !== null;
  const isView = modalMode === 'view';
  const modalTitle =
    modalMode === 'create' ? 'Create task' : modalMode === 'edit' ? 'Edit task' : 'View task';

  return (
    <div className="task-list-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div className="task-list-header">
          <div className="task-list-header-left">
            <div className="task-list-title-row">
              <Title level={3} className="task-list-title">
                Tasks
              </Title>
            </div>
            <div className="task-list-filters">
              <Input
                placeholder="Search tasks…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                allowClear
                className="task-list-search-input"
              />
              <Select
                placeholder="Status"
                allowClear
                value={statusFilter || undefined}
                onChange={(v) => {
                  setStatusFilter(v ?? '');
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                options={STATUS_OPTIONS}
                className="task-list-filter-status"
              />
              <Select
                placeholder="Assigned to"
                allowClear
                value={assignedFilter || undefined}
                onChange={(v) => {
                  setAssignedFilter(v ?? '');
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                options={userOptions}
                className="task-list-filter-assigned"
              />
            </div>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="task-list-add-btn app-btn-primary"
          >
            New task
          </Button>
        </div>

        <Card className="task-list-card">
          <Table
            className="task-list-table"
            columns={columns}
            dataSource={tasks.map((row) => ({ ...row, key: row._id }))}
            loading={loading}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No tasks yet"
                  style={{ padding: '32px 0' }}
                >
                  <Button type="primary" onClick={openCreateModal} className="app-btn-primary">
                    Create first task
                  </Button>
                </Empty>
              ),
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} tasks`,
              size: 'default',
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={onTableChange}
            size="middle"
            tableLayout="auto"
            responsive
            scroll={{ x: 1100 }}
          />
        </Card>
      </Space>

      <Modal
        title={modalTitle}
        open={isModalOpen}
        onCancel={closeModal}
        footer={
          isView
            ? [
                <Button key="close" onClick={closeModal} className="app-btn-cancel">
                  Close
                </Button>,
              ]
            : [
                <Button
                  key="ok"
                  type="primary"
                  onClick={handleEditSubmit}
                  loading={submitLoading}
                  className="app-btn-primary"
                >
                  {modalMode === 'create' ? 'Create task' : 'Save changes'}
                </Button>,
                <Button key="cancel" onClick={closeModal} className="app-btn-cancel">
                  Cancel
                </Button>,
              ]
        }
        destroyOnClose
        width={isView ? 520 : 640}
        className="task-list-modal"
      >
        {isView && selectedTask ? (
          <div className="task-list-view">
            <div className="task-list-view-hero">
              <div className="task-list-view-hero-title">{selectedTask.title || 'Untitled task'}</div>
              {selectedTask.taskType !== 'recurring' && (
                <Tag
                  className={`task-list-view-hero-status task-list-view-hero-status-${selectedTask.status || 'open'}`}
                  color={
                    selectedTask.status === 'done'
                      ? 'green'
                      : selectedTask.status === 'inprogress'
                        ? 'blue'
                        : selectedTask.status === 'on-hold'
                          ? 'orange'
                          : 'default'
                  }
                >
                  {STATUS_OPTIONS.find((o) => o.value === selectedTask.status)?.label ?? selectedTask.status ?? 'Open'}
                </Tag>
              )}
            </div>
            <div className="task-list-view-section task-list-view-section-desc">
              <div className="task-list-view-section-title">Description</div>
              {selectedTask.description && isHtml(selectedTask.description) ? (
                <div
                  className="task-list-view-description task-list-view-description-html"
                  dangerouslySetInnerHTML={{ __html: selectedTask.description }}
                />
              ) : (
                <div
                  className="task-list-view-description"
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {selectedTask.description || '—'}
                </div>
              )}
            </div>
            <div className="task-list-view-section">
              <div className="task-list-view-section-title">Details</div>
              <div className="task-list-view-row">
                <span className="task-list-view-label">Task type</span>
                <span className="task-list-view-value">
                  {TASK_TYPE_OPTIONS.find((o) => o.value === (selectedTask.taskType ?? 'one-time'))?.label ?? selectedTask.taskType ?? 'One Time'}
                </span>
              </div>
              <div className="task-list-view-row">
                <span className="task-list-view-label">Reward points</span>
                <span className="task-list-view-value">
                  {selectedTask.rewardPoint != null && selectedTask.rewardPoint !== 0
                    ? selectedTask.rewardPoint
                    : 'None'}
                </span>
              </div>
              <div className="task-list-view-row">
                <span className="task-list-view-label">Completed date</span>
                <span className="task-list-view-value">
                  {formatDate(selectedTask.completedDate)}
                </span>
              </div>
              {selectedTask.taskType !== 'recurring' && (
                <div className="task-list-view-row">
                  <span className="task-list-view-label">Status</span>
                  <span className="task-list-view-value">
                    <Tag color="blue">
                      {STATUS_OPTIONS.find((o) => o.value === selectedTask.status)?.label ??
                        selectedTask.status}
                    </Tag>
                  </span>
                </div>
              )}
              <div className="task-list-view-row">
                <span className="task-list-view-label">Assigned to</span>
                <span className="task-list-view-value">
                  {selectedTask.assignedTo?.name ||
                    selectedTask.assignedTo?.email ||
                    '—'}
                </span>
              </div>
            </div>
            {Array.isArray(selectedTask.attachments) && selectedTask.attachments.length > 0 && (
              <div className="task-list-view-section">
                <div className="task-list-view-section-title">Attachments</div>
                <ul className="task-list-view-attachments">
                  {selectedTask.attachments.map((att, i) => (
                    <li key={i}>
                      <a
                        href={isImageAttachment(att) ? '#' : (att.url?.startsWith('data:') ? '#' : att.url)}
                        target={isImageAttachment(att) || att.url?.startsWith('data:') ? undefined : '_blank'}
                        rel={isImageAttachment(att) || att.url?.startsWith('data:') ? undefined : 'noopener noreferrer'}
                        className="task-list-view-attachment-link"
                        onClick={(e) => {
                          if (isImageAttachment(att)) {
                            e.preventDefault();
                            setImagePreview({ url: att.url, name: att.name });
                          } else if (att.url?.startsWith('data:')) {
                            e.preventDefault();
                            openAttachmentUrl(att.url);
                          }
                        }}
                      >
                        <PaperClipOutlined /> {att.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="task-list-view-section">
              <div className="task-list-view-section-title">Activity</div>
              <div className="task-list-view-row">
                <span className="task-list-view-label">Created</span>
                <span className="task-list-view-value">
                  {formatDateTime(selectedTask.created_at)}
                </span>
              </div>
              <div className="task-list-view-row">
                <span className="task-list-view-label">Updated</span>
                <span className="task-list-view-value">
                  {formatDateTime(selectedTask.updated_at)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <Form form={form} layout="vertical" className="task-list-form-in-modal" initialValues={{ taskType: 'one-time' }}>
            <Row gutter={16} className="task-list-form-row">
              <Col xs={24} sm={12}>
                <Form.Item name="taskType" label="Task type" rules={[{ required: true }]}>
                  <Select options={TASK_TYPE_OPTIONS} placeholder="One Time or Recurring" allowClear={false} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="assignedTo" label="Assigned to">
                  <Select
                    placeholder="Select user"
                    options={userOptions}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="Task title" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <RichTextEditor />
            </Form.Item>
            <Row gutter={16} className="task-list-form-row">
              <Col xs={24} sm={12}>
                <Form.Item
                  name="rewardPoint"
                  label="Reward points"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select
                    placeholder="Select points"
                    options={REWARD_POINT_OPTIONS}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="completedDate" label="Completed date">
                  <DatePicker className="task-list-date-picker" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} className="task-list-form-row"> 
              <Col xs={24} sm={12}>
              <Form.Item name="attachments" label="Attachments">
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.attachments !== curr?.attachments} style={{ marginBottom: 0 }}>
                  {({ getFieldValue, setFieldsValue }) => {
                    const list = getFieldValue('attachments') || [];
                    return (
                      <Upload
                        multiple
                        maxCount={MAX_ATTACHMENTS}
                        accept="*/*"
                        listType="text"
                        className="task-list-attachments-upload"
                        beforeUpload={async (file) => {
                          if (file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) {
                            dispatch(
                              setMessage({
                                message: `File "${file.name}" is larger than ${MAX_ATTACHMENT_SIZE_MB}MB`,
                                type: 'error',
                              })
                            );
                            return Upload.LIST_IGNORE;
                          }
                          try {
                            const { url, name } = await uploadFile(file);
                            const current = getFieldValue('attachments') || [];
                            if (current.length >= MAX_ATTACHMENTS) return false;
                            setFieldsValue({ attachments: [...current, { name: name || file.name, url }] });
                          } catch (err) {
                            dispatch(setMessage({ message: 'File upload failed', type: 'error' }));
                          }
                          return false;
                        }}
                        fileList={list.map((a, i) => ({
                          uid: String(i),
                          name: a.name,
                          status: 'done',
                          url: a.url,
                        }))}
                        onRemove={(file) => {
                          const current = getFieldValue('attachments') || [];
                          const idx = current.findIndex((_, i) => String(i) === file.uid);
                          if (idx === -1) return;
                          setFieldsValue({ attachments: current.filter((_, i) => i !== idx) });
                        }}
                      >
                        <Button icon={<PaperClipOutlined />}>Add attachment</Button>
                      </Upload>
                    );
                  }}
                </Form.Item>
              </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>

      <Modal
        title={imagePreview?.name ?? 'Image'}
        open={!!imagePreview}
        onCancel={() => setImagePreview(null)}
        footer={[
          <Button key="close" onClick={() => setImagePreview(null)} className="app-btn-cancel">
            Close
          </Button>,
        ]}
        width="min(90vw, 800px)"
        className="task-list-image-preview-modal"
        destroyOnClose
      >
        {imagePreview && (
          <div className="task-list-image-preview-wrap">
            <img
              src={imagePreview.url}
              alt={imagePreview.name}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </div>
        )}
      </Modal>

      <TaskDeleteModal
        open={taskToDelete}
        onCancel={() => setTaskToDelete(null)}
        onConfirm={handleDeleteConfirm}
        task={taskToDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
